<template>
  <list-page-shell :title="$t('nav.leads')" :subtitle="$t('pipelineList.leads')">
    <template #actions>
      <el-button
        v-permission="['OFFICE','ADMIN']"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openLeadForm(null)"
      >
        {{ $t('leadListPage.create') }}
      </el-button>
    </template>

    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('leadListPage.salesOwner')" v-if="isOffice">
          <el-select v-model="filters.salesUserId" clearable :placeholder="$t('common.pleaseSelect')" style="width:180px;">
            <el-option
              v-for="s in salesList"
              :key="s.value"
              :value="s.value"
              :label="s.label"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('leadListPage.appointmentFrom')">
          <el-date-picker
            v-model="filters.appointmentDateStart"
            type="date"
            value-format="yyyy-MM-dd"
            clearable
            :placeholder="$t('common.selectDate')"
          />
        </el-form-item>

        <el-form-item :label="$t('leadListPage.appointmentTo')">
          <el-date-picker
            v-model="filters.appointmentDateEnd"
            type="date"
            value-format="yyyy-MM-dd"
            clearable
            :placeholder="$t('common.selectDate')"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch" :loading="loading">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset" :disabled="loading">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

      <el-table class="lead-list-table" :data="list" border stripe v-loading="loading" style="width:100%;">
        <el-table-column prop="leadNo" :label="$t('leadListPage.leadNo')" min-width="140"></el-table-column>

        <el-table-column :label="$t('leadListPage.contactName')" min-width="180">
          <template slot-scope="scope">
            {{
              [scope.row.firstName, scope.row.lastName].filter(Boolean).join(' ').trim() || '—'
            }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('leadListPage.salesOwner')" min-width="140">
          <template slot-scope="scope">
            <span>{{ salesDisplayName(scope.row) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('leadListPage.latestNote')"
          min-width="220"
          class-name="lead-list-col-activity"
        >
          <template slot-scope="scope">
            <div class="lead-list-activity-cell">
              <div class="lead-list-activity-body">
                <template v-if="scope.row.latestInternalNoteContent">
                  <div class="lead-list-snippet">{{ scope.row.latestInternalNoteContent }}</div>
                  <div v-if="scope.row.latestInternalNoteAt" class="lead-list-snippet-meta">
                    {{ formatDisplayDateTime(scope.row.latestInternalNoteAt) }}
                  </div>
                </template>
                <span v-else class="lead-list-snippet-meta">—</span>
              </div>
              <span
                class="lead-list-activity-add-anchor"
                v-permission="['OFFICE','ADMIN','SALES']"
              >
                <el-button
                  type="text"
                  size="mini"
                  class="lead-list-activity-icon-add"
                  icon="el-icon-plus"
                  :title="$t('leadDetailPage.noteDialog')"
                  @click.stop="openQuickNote(scope.row)"
                ></el-button>
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('leadListPage.latestFollowup')"
          min-width="240"
          class-name="lead-list-col-activity"
        >
          <template slot-scope="scope">
            <div class="lead-list-activity-cell">
              <div class="lead-list-activity-body">
                <template
                  v-if="scope.row.latestFollowupTime != null || (scope.row.latestFollowupContent != null && String(scope.row.latestFollowupContent).trim() !== '')"
                >
                  <div class="lead-list-snippet-meta">
                    {{ labelFollowupType(scope.row.latestFollowupTypeCode) }}
                    <template v-if="scope.row.latestFollowupTime"> · {{ formatDisplayDateTime(scope.row.latestFollowupTime) }}</template>
                  </div>
                  <div v-if="scope.row.latestFollowupContent && String(scope.row.latestFollowupContent).trim() !== ''" class="lead-list-snippet">
                    {{ scope.row.latestFollowupContent }}
                  </div>
                </template>
                <span v-else class="lead-list-snippet-meta">—</span>
              </div>
              <span
                class="lead-list-activity-add-anchor"
                v-permission="['OFFICE','ADMIN','SALES']"
              >
                <el-button
                  type="text"
                  size="mini"
                  class="lead-list-activity-icon-add"
                  icon="el-icon-plus"
                  :title="$t('leadDetailPage.followDialog')"
                  @click.stop="openQuickFollow(scope.row)"
                ></el-button>
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="appointmentDate" :label="$t('leadListPage.appointmentDate')" min-width="160"></el-table-column>

        <el-table-column :label="$t('common.actions')" fixed="right" min-width="500" class-name="lead-list-col-actions">
          <template slot-scope="scope">
            <div class="lead-list-actions">
              <el-button type="text" size="small" class="lead-list-action-link" @click="handleEdit(scope.row)" v-permission="['OFFICE','ADMIN','SALES']">
                {{ $t('common.edit') }}
              </el-button>
              <el-button
                type="text"
                size="small"
                class="lead-list-action-link"
                @click="openLeadQuote(scope.row)"
                v-permission="['OFFICE','ADMIN','SALES']"
              >
                {{ $t('quoteListPage.create') }}
              </el-button>
              <el-button type="text" size="small" class="lead-list-action-link" @click="handleView(scope.row)" v-permission="['OFFICE','ADMIN','SALES']">
                {{ $t('leadListPage.viewDetails') }}
              </el-button>
              <el-button type="text" size="small" class="lead-list-action-link" @click="handleAssign(scope.row)" v-permission="['OFFICE','ADMIN']">
                {{ $t('leadListPage.assignSales') }}
              </el-button>
              <el-button
                v-if="canMarkLost(scope.row)"
                type="text"
                size="small"
                class="lead-list-action-link lead-list-action-link--danger"
                @click="handleMarkLost(scope.row)"
                v-permission="['OFFICE','ADMIN','SALES']"
              >
                {{ $t('leadListPage.markLost') }}
              </el-button>
              <el-button
                type="text"
                size="small"
                class="lead-list-action-link lead-list-action-link--danger"
                @click="handleDelete(scope.row)"
                v-permission="['OFFICE','ADMIN']"
              >
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div style="margin-top:14px;text-align:right;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-sizes="[10,20,50]"
          :page-size="pageSize"
          :current-page="pageNo"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>

      <RecordFormDialog
        :visible.sync="leadFormVisible"
        :title="leadFormTitle"
      >
        <LeadForm
          v-if="leadFormVisible"
          ref="leadFormRef"
          :key="leadFormKey"
          embedded
          :record-id="leadFormRecordId"
          @saved="onLeadFormSaved"
          @saving="v => (leadFormSaving = v)"
        />
        <template slot="footer">
          <el-button @click="leadFormVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="leadFormSaving" @click="submitLeadFormDialog">{{ $t('common.save') }}</el-button>
        </template>
      </RecordFormDialog>

      <form-dialog :title="$t('leadDetailPage.noteDialog')" :visible.sync="quickNoteVisible" width="520px">
        <el-input v-model="quickNoteForm.noteContent" type="textarea" :rows="4" />
        <span slot="footer" class="dialog-footer">
          <el-button @click="quickNoteVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="quickNoteSaving" @click="saveQuickNote">{{ $t('common.save') }}</el-button>
        </span>
      </form-dialog>

      <form-dialog :title="$t('leadDetailPage.followDialog')" :visible.sync="quickFollowVisible" width="560px">
        <el-form :model="quickFollowForm" label-width="110px">
          <el-form-item :label="$t('leadDetailPage.followTime')">
            <el-date-picker
              v-model="quickFollowForm.followupTime"
              type="datetime"
              format="MM/dd/yyyy hh:mm:ss A"
              value-format="yyyy-MM-dd HH:mm:ss"
              :placeholder="$t('leadDetailPage.followTimePh')"
              style="width:100%;"
              clearable
            />
          </el-form-item>
          <el-form-item :label="$t('leadDetailPage.followType')">
            <el-select v-model="quickFollowForm.followupTypeCode" :placeholder="$t('common.pleaseSelect')" style="width:100%;">
              <el-option v-for="o in followTypeOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('leadDetailPage.followContent')">
            <el-input v-model="quickFollowForm.content" type="textarea" :rows="4" :placeholder="$t('leadDetailPage.followContentPh')" />
          </el-form-item>
          <el-form-item :label="$t('leadDetailPage.nextAppt')">
            <el-date-picker
              v-model="quickFollowForm.nextAppointmentDate"
              type="date"
              value-format="yyyy-MM-dd"
              :placeholder="$t('common.optional')"
              style="width:100%;"
              clearable
            />
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="quickFollowVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="quickFollowSaving" @click="saveQuickFollow">{{ $t('common.save') }}</el-button>
        </span>
      </form-dialog>

      <LeadQuoteDialog :visible.sync="leadQuoteDialogVisible" :lead-id="leadQuoteLeadId" @saved="fetchList" />

      <form-dialog :title="$t('leadListPage.assignDialogTitle')" :visible.sync="assignDialogVisible" width="420px">
        <el-form :model="assignForm" label-width="110px">
          <el-form-item :label="$t('leadListPage.salesOwner')">
            <el-select v-model="assignForm.assignedSalesUserId" clearable :placeholder="$t('common.pleaseSelect')">
              <el-option
                v-for="s in salesList"
                :key="s.value"
                :value="s.value"
                :label="s.label"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="assignDialogVisible=false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="assignSubmitting" @click="confirmAssign">{{ $t('common.confirm') }}</el-button>
        </span>
      </form-dialog>
  </list-page-shell>
</template>

<script>
import { getLeadPage, assignLead, markLeadLost, deleteLead, addInternalNote, addFollowup } from '@/api/lead'
import { getSalesUserList } from '@/api/user'
import { enumSelectOptions, labelFollowupType } from '@/utils/crmLabels'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'
import ListPageShell from '@/components/ListPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import FormDialog from '@/components/FormDialog.vue'
import LeadForm from '@/views/lead/LeadForm.vue'
import LeadQuoteDialog from '@/views/lead/LeadQuoteDialog.vue'

export default {
  name: 'LeadList',
  components: { ListPageShell, RecordFormDialog, FormDialog, LeadForm, LeadQuoteDialog },
  data() {
    return {
      leadFormVisible: false,
      leadFormSaving: false,
      leadFormRecordId: null,
      leadFormKey: 0,
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: {
        salesUserId: '',
        appointmentDateStart: '',
        appointmentDateEnd: ''
      },
      salesList: [],
      isOffice: false,
      assignDialogVisible: false,
      assignSubmitting: false,
      assignForm: {
        leadId: null,
        assignedSalesUserId: null
      },
      quickNoteLeadId: null,
      quickNoteVisible: false,
      quickNoteSaving: false,
      quickNoteForm: { noteContent: '' },
      quickFollowLeadId: null,
      quickFollowVisible: false,
      quickFollowSaving: false,
      quickFollowForm: {
        content: '',
        followupTypeCode: 1,
        followupTime: null,
        nextAppointmentDate: null
      },
      leadQuoteDialogVisible: false,
      leadQuoteLeadId: null
    }
  },
  computed: {
    leadFormTitle() {
      return this.leadFormRecordId
        ? this.$t('leadFormPage.editTitle')
        : this.$t('leadFormPage.newTitle')
    },
    followTypeOptions() {
      void this.$locale
      return enumSelectOptions(this.$t, 'followupType', [1, 2, 3, 5, 4])
    }
  },
  async created() {
    const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
    this.isOffice = roles.includes('OFFICE') || roles.includes('ADMIN')

    if (this.isOffice) {
      await this.loadSalesList()
    }

    this.fetchList()
  },
  methods: {
    async loadSalesList() {
      try {
        const resp = await getSalesUserList()
        const rows = (resp && resp.list) || []
        this.salesList = rows.map(r => ({
          value: r.id,
          label: this.formatSalesOptionLabel(r)
        }))
      } catch (e) {
        this.salesList = []
        this.$message.error((e && e.message) || this.$t('leadListPage.loadSalesFailed'))
      }
    },

    formatSalesOptionLabel(r) {
      const name = (r.realName || r.username || '').trim()
      return name || this.$t('leadListPage.unnamed')
    },

    salesDisplayName(row) {
      if (!row) return '—'
      if (row.assignedSalesUserName) return row.assignedSalesUserName
      const id = row.assignedSalesUserId
      if (id === null || id === undefined || id === '') return '—'
      const hit = this.salesList.find(s => Number(s.value) === Number(id))
      return hit ? hit.label : '—'
    },

    labelFollowupType,
    formatDisplayDateTime,

    formatNowDateTime() {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },

    openQuickNote(row) {
      this.quickNoteLeadId = row.id
      this.quickNoteForm = { noteContent: '' }
      this.quickNoteVisible = true
    },

    async saveQuickNote() {
      if (!this.quickNoteForm.noteContent || !String(this.quickNoteForm.noteContent).trim()) {
        this.$message.warning(this.$t('leadDetailPage.needNote'))
        return
      }
      this.quickNoteSaving = true
      try {
        await addInternalNote(this.quickNoteLeadId, this.quickNoteForm)
        this.quickNoteVisible = false
        this.$message.success(this.$t('common.saved'))
        this.fetchList()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.quickNoteSaving = false
      }
    },

    openQuickFollow(row) {
      this.quickFollowLeadId = row.id
      this.quickFollowForm = {
        content: '',
        followupTypeCode: 1,
        followupTime: this.formatNowDateTime(),
        nextAppointmentDate: null
      }
      this.quickFollowVisible = true
    },

    async saveQuickFollow() {
      if (!this.quickFollowForm.content || !String(this.quickFollowForm.content).trim()) {
        this.$message.warning(this.$t('leadDetailPage.needContent'))
        return
      }
      this.quickFollowSaving = true
      try {
        const payload = {
          content: this.quickFollowForm.content,
          followupTypeCode: this.quickFollowForm.followupTypeCode
        }
        if (this.quickFollowForm.followupTime) {
          payload.followupTime = this.quickFollowForm.followupTime
        }
        if (this.quickFollowForm.nextAppointmentDate) {
          payload.nextAppointmentDate = this.quickFollowForm.nextAppointmentDate
        }
        await addFollowup(this.quickFollowLeadId, payload)
        this.quickFollowVisible = false
        this.$message.success(this.$t('common.saved'))
        this.fetchList()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.quickFollowSaving = false
      }
    },

    async fetchList() {
      this.loading = true
      try {
        const params = {
          pageNo: this.pageNo,
          pageSize: this.pageSize
        }
        if (this.isOffice && this.filters.salesUserId !== '') params.salesUserId = this.filters.salesUserId
        if (this.filters.appointmentDateStart) params.appointmentStartDate = this.filters.appointmentDateStart
        if (this.filters.appointmentDateEnd) params.appointmentEndDate = this.filters.appointmentDateEnd

        const resp = await getLeadPage(params)
        this.list = resp.records || []
        this.total = resp.total || 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pageNo = 1
      this.fetchList()
    },

    handleReset() {
      this.filters = {
        salesUserId: '',
        appointmentDateStart: '',
        appointmentDateEnd: ''
      }
      this.pageNo = 1
      this.fetchList()
    },

    openLeadForm(id) {
      this.leadFormRecordId = id != null ? id : null
      this.leadFormKey += 1
      this.leadFormVisible = true
    },
    onLeadFormSaved() {
      this.leadFormVisible = false
      this.fetchList()
    },
    submitLeadFormDialog() {
      if (this.$refs.leadFormRef) this.$refs.leadFormRef.submit()
    },
    handleEdit(row) {
      this.openLeadForm(row.id)
    },

    canMarkLost(row) {
      if (!row) return false
      const status = Number(row.leadStatusCode)
      return status !== 4 && status !== 5
    },

    async handleMarkLost(row) {
      try {
        await this.$confirm(this.$t('leadListPage.confirmMarkLost'), this.$t('common.tip'), { type: 'warning' })
        await markLeadLost(row.id)
        this.$message.success(this.$t('common.saved'))
        this.fetchList()
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
          this.$message.error((e && e.message) || this.$t('common.failed'))
        }
      }
    },

    handleView(row) {
      this.$router.push(`/lead/detail/${row.id}`)
    },

    openLeadQuote(row) {
      this.leadQuoteLeadId = row.id
      this.leadQuoteDialogVisible = true
    },

    handleAssign(row) {
      this.assignForm.leadId = row.id
      const cur = row.assignedSalesUserId
      this.assignForm.assignedSalesUserId =
        cur !== null && cur !== undefined && cur !== '' ? Number(cur) : null
      this.assignDialogVisible = true
    },

    async confirmAssign() {
      const { leadId, assignedSalesUserId } = this.assignForm
      if (!leadId || assignedSalesUserId === null || assignedSalesUserId === '') {
        this.$message.warning(this.$t('leadListPage.selectSalesOwnerWarn'))
        return
      }
      this.assignSubmitting = true
      try {
        await assignLead(leadId, assignedSalesUserId)
        this.assignDialogVisible = false
        this.$message.success(this.$t('leadListPage.assignSuccess'))
        this.fetchList()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('leadListPage.assignFailed'))
      } finally {
        this.assignSubmitting = false
      }
    },

    async handleDelete(row) {
      try {
        await this.$confirm(this.$t('leadListPage.confirmDelete'), this.$t('common.tip'), { type: 'warning' })
        await deleteLead(row.id)
        this.$message.success(this.$t('leadListPage.deleteSuccess'))
        this.fetchList()
      } catch (e) {
        // 取消不提示
      }
    },

    handleCurrentChange(pageNo) {
      this.pageNo = pageNo
      this.fetchList()
    },

    handleSizeChange(pageSize) {
      this.pageSize = pageSize
      this.pageNo = 1
      this.fetchList()
    }
  }
}
</script>

<style scoped>
/* 操作列：单行、左对齐连续排列；列窄时横向滚动不换行（勿给商机按钮设 min-width，短文案会在格内留出大块空白） */
.lead-list-actions {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  column-gap: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  width: max-content;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.lead-list-actions-opp {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}

.lead-list-action-link {
  padding-left: 6px !important;
  padding-right: 6px !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.lead-list-action-link--danger {
  color: #f56c6c !important;
}

.lead-list-snippet {
  color: #cbd5e1;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.lead-list-snippet-meta {
  margin-top: 2px;
  color: var(--dark-text-sub, #8b95a1);
  font-size: 11px;
  line-height: 1.35;
}

/*
 * 内层禁止 position:relative，否则加号会相对「文字块」而非整格。
 * 定位参照仅为下方全局样式中的 td.lead-list-col-activity .cell
 */
.lead-list-activity-cell {
  min-width: 0;
}

.lead-list-activity-body {
  min-width: 0;
}

.lead-list-activity-add-anchor {
  position: absolute !important;
  top: 0;
  right: 4px;
  left: auto !important;
  bottom: auto !important;
  width: 22px;
  height: 22px;
  z-index: 2;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  float: none !important;
}

.lead-list-activity-icon-add {
  position: static !important;
  padding: 0 !important;
  margin: 0 !important;
  min-height: auto !important;
  height: 18px !important;
  width: 18px !important;
  line-height: 1 !important;
  font-size: 12px !important;
  color: #5c6570 !important;
  border: none !important;
}

.lead-list-activity-icon-add >>> .el-icon-plus {
  font-size: 12px;
  font-weight: 600;
}

.lead-list-activity-icon-add:hover,
.lead-list-activity-icon-add:focus,
.lead-list-activity-icon-add:active {
  color: #8b95a1 !important;
  background: transparent !important;
}

</style>

<style>
/* 加号相对「单元格 .cell」的 padding 盒右上角，与正文块无包含关系 */
.lead-list-table td.lead-list-col-activity {
  vertical-align: top !important;
}

.lead-list-table td.lead-list-col-activity > .cell {
  position: relative !important;
  overflow: visible !important;
  padding-right: 32px !important;
}

.lead-list-table td.lead-list-col-actions > .cell {
  overflow-x: auto;
  overflow-y: hidden;
}
</style>
